import {
  milestoneIsClosed,
  milestoneIsInApproval,
  milestoneIsSkipped,
} from "@/containers/Project/components/Milestones/components/ApproversCard/helper/milestoneStatusHelper";
import { MilestoneErrorModal } from "@/containers/Project/components/Milestones/components/Modals/MilestoneErrorModal";
import { useDeleteMilestonePermission } from "@/containers/Project/components/Milestones/hooks/useDeleteMilestonePermission";
import {
  FieldNames,
  FormType,
  ProjectMilestoneStatus,
  ProjectMilestoneStatusActions,
  ProjectUserPermissions,
} from "@/enums";
import { findTargetMilestoneRecursive } from "@/helpers";
import { useIsAdmin } from "@/helpers/permissions";
import { IMilestone, ISchedule } from "@/interfaces";
import {
  useFormPropertyValueLookupProvider,
  useProjectContext,
} from "@/providers";
import {
  useDeleteProjectMilestoneMutation,
  useGetProjectTemplateQuery,
} from "@/services";
import { Button, Modal } from "antd";
import { useState } from "react";
import { useIntl } from "react-intl";
import { IErrorProps } from "../interfaces";
import { MilestoneActionButton } from "./styled";

interface IProps {
  milestone: IMilestone;
  childLevel: number;
  maxChildLevel: number;
  parentId: string;
}

const MilestoneActionButtons = ({
  milestone,
  childLevel,
  maxChildLevel,
  parentId,
}: IProps) => {
  const intl = useIntl();
  const {
    openMilestoneModal,
    isUserProjectLeader,
    canUserModifyProject,
    setEditMilestone,
    setMilestoneStatusActionCommentModal,
    currentSchedule,
    milestoneStatusChange,
    deleteMilestone,
    canUser,
    project,
  } = useProjectContext();

  const { getPropertyFallback } = useFormPropertyValueLookupProvider();

  const canDeleteMilestone =
    useDeleteMilestonePermission(milestone) && milestone.status !== "Approved";
  const isAdmin = useIsAdmin();

  const [_, { isLoading: isDeleting }] = useDeleteProjectMilestoneMutation({
    fixedCacheKey: "delete-project-milestone",
  });

  const [confirmDeleteVisible, setConfirmDeleteVisible] =
    useState<boolean>(false);
  const [errorModal, setErrorModal] = useState<IErrorProps>({
    visible: false,
    title: "",
    description: "",
    errors: [],
  });

  const canEditMilestone = canUser(
    ProjectUserPermissions.ViewMilestoneDropdownOptions,
    milestone
  );

  const canAddMilestone = canUser(
    ProjectUserPermissions.AddMilestones,
    milestone
  );

  const canSkipMilestone =
    milestone.canBeSkipped &&
    !milestoneIsSkipped(milestone) &&
    canEditMilestone &&
    milestone.status !== "Approved";

  const isComplete =
    getPropertyFallback(FormType.Intake, FieldNames.Status, project.Status) ===
    "Complete";

  const { data: projectTemplate } = useGetProjectTemplateQuery(
    project.ProjectTemplateId,
    {
      skip: !project?.ProjectTemplateId,
    }
  );

  const canReenable =
    milestoneIsSkipped(milestone) &&
    canEditMilestone &&
    (isUserProjectLeader() || isAdmin);

  const canResetStatus =
    (milestoneIsClosed(milestone) || milestoneIsInApproval(milestone)) &&
    (canUser(ProjectUserPermissions.ResetCompleteMilestones) ||
      (canUserModifyProject() && !milestone.shouldCompleteProject));

  const checkParentStatus = (status: string): boolean => {
    if (project?.schedules?.length > 0) {
      const targetSchedule = project.schedules.find(
        (schedule: ISchedule) => schedule.id === currentSchedule
      );

      if (targetSchedule?.milestones?.length > 0) {
        const parent = findTargetMilestoneRecursive(
          targetSchedule.milestones,
          parentId
        ) as IMilestone;
        return parent?.status === status;
      }
    }
    return false;
  };

  const validateAndConfirmDelete = (): void => {
    let errors = [];
    if (isAdmin) {
      errors = verifyCanAdminDeleteMilestoneRecursive(
        milestone.childMilestones,
        []
      );
    } else {
      errors = verifyCanDeleteMilestoneRecursive(milestone, []);
    }

    if (errors?.length > 0) {
      setErrorModal({
        visible: true,
        title: intl.formatMessage(
          { id: `milestonesList.milestoneDeleteErrorTitle` },
          { milestoneName: milestone.displayName || milestone.name }
        ),
        description: intl.formatMessage({
          id: `milestonesList.milestoneDeleteErrorDescription`,
        }),
        errors: errors,
      });
    } else {
      setConfirmDeleteVisible(true);
    }
  };

  const handleCloseModal = (): void => {
    setErrorModal({
      visible: false,
      title: "",
      description: "",
      errors: [],
    });
  };

  const verifyCanAdminDeleteMilestoneRecursive = (
    childMilestones: IMilestone[],
    errors: string[]
  ): string[] => {
    if (childMilestones?.length > 0) {
      childMilestones.forEach((newMilestone) => {
        errors = verifyCanDeleteMilestoneRecursive(newMilestone, errors);
      });
    }

    return errors;
  };

  const verifyCanDeleteMilestoneRecursive = (
    targetMilestone: IMilestone,
    errors: string[]
  ): string[] => {
    const targetMilestoneName =
      targetMilestone.displayName || targetMilestone.name;
    if (targetMilestone.status !== ProjectMilestoneStatus.Draft) {
      errors.push(
        intl.formatMessage(
          { id: `milestonesList.milestoneDeleteStatusError` },
          { milestoneName: targetMilestoneName }
        )
      );
    }

    if (targetMilestone.childMilestones?.length > 0) {
      targetMilestone.childMilestones.forEach((child: IMilestone) => {
        verifyCanDeleteMilestoneRecursive(child, errors);
      });
    }

    return errors;
  };

  const hasChildMilestonesNotInDraft = (milestone: IMilestone): boolean => {
    if (isAdmin) {
      return (
        verifyCanAdminDeleteMilestoneRecursive(milestone.childMilestones, [])
          ?.length > 0
      );
    }
    return verifyCanDeleteMilestoneRecursive(milestone, [])?.length > 0;
  };

  const handleMenuActions = (e: IMenuItem) => {
    switch (e.key) {
      case "reset":
        milestoneStatusChange(
          project.id,
          milestone.id,
          ProjectMilestoneStatusActions.Reset
        );
        break;
      case "reenable":
        setMilestoneStatusActionCommentModal({
          milestoneId: milestone.id,
          targetStatus: ProjectMilestoneStatus.Reenable,
          finalApprover: false,
        });
        break;
      case "edit":
        setEditMilestone(milestone.id);
        break;
      case "delete":
        validateAndConfirmDelete();
        break;
      case "add":
        openMilestoneModal(milestone.id);
        break;
      case "skip":
        setMilestoneStatusActionCommentModal({
          milestoneId: milestone.id,
          targetStatus: ProjectMilestoneStatus.Skipped,
          finalApprover: false,
        });
        break;
      case "vigorTemplate":
        window.open(projectTemplate.vigorTemplateUrl);
        break;
      default:
        break;
    }
  };

  interface IMenuItem {
    key: string;
    title: string;
    disabled?: boolean;
    visible?: boolean;
  }

  const menuItems: IMenuItem[] = [
    {
      key: "reset",
      title: intl.formatMessage({ id: "milestonesList.moveToDraft" }),
      visible: canResetStatus,
    },
    {
      key: "reenable",
      title: intl.formatMessage({ id: "milestonesList.reenable" }),
      disabled: !!parentId && checkParentStatus(ProjectMilestoneStatus.Skipped),
      visible: canReenable,
    },
    {
      key: "skip",
      title: intl.formatMessage({ id: "milestonesList.skip" }),
      visible: canSkipMilestone && !hasChildMilestonesNotInDraft(milestone),
    },
    {
      key: "delete",
      title: intl.formatMessage({ id: "milestonesList.delete" }),
      visible:
        canDeleteMilestone &&
        canEditMilestone &&
        !hasChildMilestonesNotInDraft(milestone),
    },
    {
      key: "add",
      title: intl.formatMessage({ id: "milestonesList.addMilestone" }),
      visible: childLevel < maxChildLevel && canAddMilestone,
    },
    {
      key: "edit",
      title: intl.formatMessage({ id: "milestonesList.edit" }),
      visible: canEditMilestone,
    },
    {
      key: "vigorTemplate",
      title: intl.formatMessage({
        id: "project.vigorTemplatesLabel",
      }),
      visible: projectTemplate?.vigorTemplateUrl?.length > 0,
    },
  ];

  const displayMenuItems = (items: IMenuItem[]) => {
    const visibleMenuItems = items.filter(
      (menuItem) => menuItem.visible === undefined || menuItem.visible
    );

    return visibleMenuItems.map((menuItem) => (
      <MilestoneActionButton
        type="text"
        data-test-id={`milestone-action-${menuItem.key}`}
        key={menuItem.key}
        onClick={(): void => handleMenuActions(menuItem)}
      >
        {menuItem.title}
      </MilestoneActionButton>
    ));
  };

  return (
    <>
      {milestone && (
        <>
          {!isComplete ? (
            displayMenuItems(menuItems)
          ) : (
            <div>
              {projectTemplate?.vigorTemplateUrl?.length > 0 && (
                <Button
                  data-test-id={`vigor-templates-button-${milestone.id}`}
                  key={`vigor-templates-button-${milestone.id}`}
                  onClick={(): Window | null =>
                    window.open(projectTemplate.vigorTemplateUrl)
                  }
                >
                  {intl.formatMessage({
                    id: "project.vigorTemplatesLabel",
                  })}
                </Button>
              )}
            </div>
          )}
          <Modal
            open={confirmDeleteVisible}
            onOk={(): void => {
              deleteMilestone(milestone.id);
              setConfirmDeleteVisible(false);
            }}
            onCancel={(): void => setConfirmDeleteVisible(false)}
            title={intl.formatMessage(
              { id: "milestonesList.confirmDeleteProjectMilestoneTitle" },
              { milestoneName: milestone.name }
            )}
            okText={intl.formatMessage({ id: "general.yes" })}
            confirmLoading={isDeleting}
          >
            {intl.formatMessage({
              id: "milestonesList.confirmDeleteProjectMilestoneBody",
            })}
          </Modal>
          <MilestoneErrorModal
            modalState={errorModal}
            closeModal={handleCloseModal}
          />
        </>
      )}
    </>
  );
};

export { MilestoneActionButtons };
